// 왼쪽 필터 레일 관련 컴포넌트 모음
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import styles from "../IntegratedSearchPage.module.css";
import { labelFor, FILTER_GROUPS } from "../data/searchData";
import { Icon } from "./Icons";

/* 장기를 고르기 전까지 잠기는 필드 래퍼.
 * 자식은 pointer-events:none 이라 클릭이 이 div 로 올라오고, 여기서 안내 팝업을 띄운다.
 * (disabled 로 막으면 클릭 자체가 안 잡혀 안내를 못 준다) */
export function LockedWrap({ locked, onBlocked, children }) {
    if (!locked) return children;
    return (
        <div className={styles.fieldLocked} onClick={onBlocked} title="">
            {children}
        </div>
    );
}

/* =============================================================
 * Filter rail — collapsed
 * ============================================================= */
export function CollapsedRail({ onExpand, totalActive, resultCount, totalSamples }) {
    const { t } = useTranslation();
    return (
        <aside className={styles.railCollapsed}>
            <button className={styles.railToggleBtn} title={t('필터 펼치기')} onClick={onExpand}><Icon.Filter /></button>
            {totalActive > 0 && <span className={styles.railToggleBadge}>{totalActive}</span>}
            <div className={styles.railDivider} />
            {FILTER_GROUPS.map((g) => {
                const I = Icon[g.icon];
                return <button key={g.key} className={styles.railGroupIcon} title={t(g.label)} onClick={onExpand}><I /></button>;
            })}
            <div className={styles.railSpacer} />
            <div className={styles.railCountSmall}>
                <div className={styles.railCountNum}>{resultCount}</div>
                <div className={styles.railCountTotal}>/ {totalSamples}</div>
            </div>
        </aside>
    );
}

/* =============================================================
 * Filter rail — expanded
 * ============================================================= */
export function FilterRail({
    openGroups, setOpenGroups, chips, ranges, toggleChip, setRange,
    totalActive, clearAll, cohorts, applyCohort, onManageCohorts,
    onCollapse, resultCount, totalSamples, dynamicSubtypes,
    dynamicMutGenes, dynamicCnvGenes,
    dynamicDrug, dynamicTxDrug, dynamicMutRepGenes, dynamicOrgans,
    mutState, cycleMutation, hgvsp, toggleHgvsp, includedGenes, hgvspByGene,
    cnv, addCnvGene, setCnvType, removeCnvGene,
}) {
    const { t } = useTranslation();
    // 장기를 하나라도 골랐는지. 고르기 전에는 organLock 필드를 잠근다.
    const organSelected = (chips.organ || []).length > 0;
    const warnOrganFirst = () => {
        Swal.fire({
            icon: 'info',
            title: t('장기를 먼저 선택해주세요'),
            text: t('장기를 고르면 나머지 필터가 열립니다.'),
            confirmButtonText: t('확인'),
        });
    };
    const pct = totalSamples ? Math.round((resultCount / totalSamples) * 100) : 0;
    const groupActiveCount = (g) =>
        g.fields.reduce((s, f) => {
            if (f.kind === "chips") return s + (chips[f.key] || []).length;
            if (f.kind === "range") return s + (ranges[f.key] ? 1 : 0);
            if (f.kind === "genevariant") return s + Object.keys(mutState).length + Object.values(hgvsp).reduce((a, b) => a + b.length, 0);
            if (f.kind === "cnvgene") return s + cnv.length;
            return s;
        }, 0);

    const resolveOptions = (f) => {
        // 장기 목록은 백엔드가 정본. 새 장기(방광·분변 등)가 들어와도 필터에 뜬다.
        if (f.key === "organ" && dynamicOrgans?.length) return dynamicOrgans;
        if (f.dynamic === "subtype") return dynamicSubtypes;
        if (f.dynamic === "mutGene") return dynamicMutGenes;
        if (f.dynamic === "cnvGene") return dynamicCnvGenes;
        if (f.dynamic === "drug") return dynamicDrug;
        if (f.dynamic === "txDrug") return dynamicTxDrug;
        return f.options;
    };
    // 대표 유전자 버튼: 백엔드 ORGAN_MUTATIONS 가 정본. 없으면 field.repGenes(정적 폴백).
    const resolveRepGenes = (f) =>
        (f.dynamic === "mutGene" && dynamicMutRepGenes?.length) ? dynamicMutRepGenes : f.repGenes;

    return (
        <aside className={styles.railExpanded}>
            <div className={styles.railInner}>
                <div className={styles.railHeader}>
                    <div className={styles.railHeaderRow}>
                        <span className={styles.railTitle}>
                            <Icon.Filter /> {t('필터')}
                            {totalActive > 0 && <span className={styles.railHeaderBadge}>{totalActive}</span>}
                        </span>
                        <button className={styles.iconBtn} title={t('필터 접기')} onClick={onCollapse}><Icon.ChevL /></button>
                    </div>
                    {!organSelected && (
                        <div className={styles.organFirstNote}>{t('장기를 먼저 선택해주세요')}</div>
                    )}

                    <div className={styles.countTile}>
                        <div>
                            <div className={styles.countNum}>{resultCount}</div>
                            <div className={styles.countLabel}>{t('전체')} <span className={styles.mono}>{totalSamples}</span> {t('그룹 중')}</div>
                        </div>
                        <div className={styles.countCircleWrap}>
                            <svg viewBox="0 0 36 36" width="36" height="36">
                                <circle cx="18" cy="18" r="15" className={styles.countCircleBg} />
                                <circle cx="18" cy="18" r="15" className={styles.countCircleFg}
                                    strokeDasharray={`${(pct / 100) * 94} 94`} transform="rotate(-90 18 18)" />
                            </svg>
                            <div className={styles.countCircleLabel}>{pct}%</div>
                        </div>
                    </div>

                    {totalActive > 0 && <button className={styles.linkBtn} onClick={clearAll}>{t('필터 전체 해제')}</button>}
                </div>

                <div className={styles.savedSection}>
                    <div className={styles.savedHeader}>
                        <span className={styles.sectionLabel}>{t('저장된 코호트')}</span>
                        <button className={styles.linkBtn} onClick={onManageCohorts}>{t('관리')}</button>
                    </div>
                    <div className={styles.savedList}>
                        {cohorts.map((c) => (
                            <button key={c.id} className={styles.savedItem} title={c.name} onClick={() => applyCohort(c)}>
                                <span className={styles.savedItemLeft}><Icon.Star /> <span className={styles.savedItemName}>{c.name}</span></span>
                            </button>
                        ))}
                        {cohorts.length === 0 && <span className={styles.savedEmpty}>{t('저장된 코호트 없음')}</span>}
                    </div>
                </div>

                <div className={styles.groupsList}>
                    {FILTER_GROUPS.map((group) => {
                        const I = Icon[group.icon];
                        const isOpen = !!openGroups[group.key];
                        const active = groupActiveCount(group);
                        return (
                            <div key={group.key} className={styles.groupItem}>
                                <button className={styles.groupHeader}
                                    onClick={() => setOpenGroups((p) => ({ ...p, [group.key]: !p[group.key] }))}>
                                    <span className={styles.groupHeaderLeft}>
                                        <I /> {t(group.label)}
                                        {active > 0 && <span className={styles.groupBadge}>{active}</span>}
                                    </span>
                                    <span className={isOpen ? styles.chevOpen : styles.chevClosed}><Icon.Chevron /></span>
                                </button>
                                {isOpen && (
                                    <div className={styles.groupFields}>
                                        {group.fields.map((f) => (
                                            <LockedWrap key={f.key} locked={!!f.organLock && !organSelected} onBlocked={warnOrganFirst}>
                                                <FieldControl organNames={chips.organ || []}
                                                    field={{ ...f, options: resolveOptions(f), repGenes: resolveRepGenes(f) }}
                                                    chipValue={chips[f.key] || []}
                                                    rangeValue={ranges[f.key] || f.value}
                                                    onToggle={(v) => toggleChip(f.key, v, f.single)}
                                                    onRange={(r) => setRange(f.key, r)}
                                                    mutState={mutState} cycleMutation={cycleMutation}
                                                    hgvsp={hgvsp} toggleHgvsp={toggleHgvsp}
                                                    includedGenes={includedGenes} hgvspByGene={hgvspByGene}
                                                    cnv={cnv} addCnvGene={addCnvGene} setCnvType={setCnvType} removeCnvGene={removeCnvGene}
                                                />
                                            </LockedWrap>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </aside>
    );
}

export function FieldControl({ organNames = [], field, chipValue, rangeValue, onToggle, onRange, mutState, cycleMutation, hgvsp, toggleHgvsp, includedGenes, hgvspByGene, cnv, addCnvGene, setCnvType, removeCnvGene }) {
    const { t } = useTranslation();
    // ── mutation 3-state + hgvsp_short (검색으로 유전자 찾기 지원) ──
    if (field.kind === "genevariant") {
        return (
            <MutationSelect field={field} mutState={mutState} cycleMutation={cycleMutation}
                includedGenes={includedGenes} hgvsp={hgvsp} toggleHgvsp={toggleHgvsp} hgvspByGene={hgvspByGene} />
        );
    }

    // ── CNV: 유전자 검색·선택 후 Amplification / Deletion 지정 ──
    if (field.kind === "cnvgene") {
        return (
            <CnvSelect field={field} cnv={cnv} addCnvGene={addCnvGene} setCnvType={setCnvType} removeCnvGene={removeCnvGene} />
        );
    }

    if (field.kind === "chips") {
        if (!field.options || field.options.length === 0) {
            // 장기 미선택(= 아직 옵션을 못 만듦)과 장기 선택 후 데이터 없음을 구분한다.
            // 후자는 오류가 아니라 사실이므로 장기명을 넣어 단정적으로 알린다.
            const isEmptyResult = organNames.length > 0;
            return (
                <div>
                    <div className={styles.fieldLabel}>{t(field.label)}</div>
                    <div className={styles.emptyNote}>
                        {isEmptyResult
                            ? `${organNames.join(", ")} — ${t('데이터가 없습니다')}`
                            : t('장기를 먼저 선택해주세요')}
                    </div>
                </div>
            );
        }
        // 드롭다운+체크박스 (subtype 등)
        if (field.dropdown) {
            return <DropdownCheckSelect field={field} chipValue={chipValue} onToggle={onToggle} />;
        }
        // 옵션이 많은 필드는 상위 N개만 + 검색으로 더 찾기
        return <ChipSelect field={field} chipValue={chipValue} onToggle={onToggle} />;
    }
    if (field.kind === "range") {
        const [lo, hi] = rangeValue;
        const { min, max, step = 1 } = field;
        const pctLo = ((lo - min) / (max - min)) * 100;
        const pctHi = ((hi - min) / (max - min)) * 100;
        const fmt = (v) => (step < 1 ? (+v).toFixed(2) : v);
        return (
            <div>
                <div className={styles.fieldLabel}>{t(field.label)}</div>
                <div className={styles.rangeRow}>
                    <span className={styles.mono10}>{fmt(lo)}</span>
                    <span className={styles.rangeDash}>—</span>
                    <span className={styles.mono10}>{fmt(hi)}</span>
                </div>
                <div className={styles.dualRange}>
                    <div className={styles.dualTrack} />
                    <div className={styles.dualFill} style={{ left: `${pctLo}%`, right: `${100 - pctHi}%` }} />
                    <input type="range" min={min} max={max} step={step} value={lo}
                        className={`${styles.dualThumb} ${styles.dualThumbLo}`}
                        onChange={(e) => onRange([Math.min(+e.target.value, hi), hi])} />
                    <input type="range" min={min} max={max} step={step} value={hi}
                        className={`${styles.dualThumb} ${styles.dualThumbHi}`}
                        onChange={(e) => onRange([lo, Math.max(+e.target.value, lo)])} />
                </div>
            </div>
        );
    }
    return null;
}

/* mutation 3-state 선택 (대표 유전자 버튼 고정 + 검색으로 전체 유전자 추가) */
export function MutationSelect({ field, mutState, cycleMutation, includedGenes, hgvsp, toggleHgvsp, hgvspByGene }) {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const opts = field.options || [];           // 검색용 전체 유전자(백엔드 mutGenes)
    const repGenes = field.repGenes || [];       // 대표 유전자(고정 버튼)
    const ql = q.trim().toLowerCase();

    const searchHits = ql
        ? opts.filter((g) => repGenes.indexOf(g) === -1 && !mutState[g] && g.toLowerCase().includes(ql)).slice(0, 30)
        : [];
    // 검색으로 추가된(대표 아님) + 현재 선택된 유전자
    const extraChosen = Object.keys(mutState).filter((g) => repGenes.indexOf(g) === -1);

    const renderChip = (g) => {
        const st = mutState[g];
        const cls = st === "include" ? `${styles.chip} ${styles.chipInclude}`
            : st === "exclude" ? `${styles.chip} ${styles.chipExclude}` : styles.chip;
        return (
            <button key={g} className={cls} onClick={() => cycleMutation(g)} title={t('클릭: 포함 → 제외 → 해제')}>
                {st === "exclude" && <span className={styles.notMark}>NOT</span>} {g}
            </button>
        );
    };

    return (
        <div>
            <div className={styles.fieldLabel}>{t(field.label)}</div>
            <div className={styles.chipSearch}>
                <Icon.Search />
                <input className={styles.chipSearchInput} placeholder={t('유전자 검색 (전체)')}
                    value={q} onChange={(e) => setQ(e.target.value)} />
                {q && <button className={styles.chipSearchClear} onClick={() => setQ("")}><Icon.X /></button>}
            </div>
            {ql && (
                /* 선택해도 검색어를 지우지 않는다 → 목록이 열린 채로 남아 연속 선택이 된다.
                 * (예전에는 setQ("") 로 목록이 닫혀 매번 다시 검색해야 했다) */
                <div className={styles.chipSearchResult}>
                    {searchHits.length > 0 ? searchHits.map((g) => (
                        <button key={g} className={styles.chipSearchHit} onClick={() => cycleMutation(g)}>
                            <Icon.Plus /> {g}
                        </button>
                    )) : <span className={styles.fieldHint}>{t('검색 결과 없음')}</span>}
                </div>
            )}
            {/* 검색으로 추가된 비대표 유전자 (해제하면 mutState 에서 빠져 자동으로 사라짐) */}
            {extraChosen.length > 0 && <div className={styles.chipRow}>{extraChosen.map(renderChip)}</div>}
            {/* 대표 유전자 (항상 고정 노출) */}
            <div className={styles.chipRow} style={extraChosen.length > 0 ? { marginTop: 4 } : undefined}>
                {repGenes.map(renderChip)}
            </div>
            {/* 선택(include) 유전자별 hgvsp_short 2차 필터 */}
            {includedGenes.map((g) => (
                <HgvspPanel key={g} gene={g} variants={hgvspByGene[g] || []}
                    selected={hgvsp[g] || []} onToggle={(v) => toggleHgvsp(g, v)} />
            ))}
        </div>
    );
}

/* CNV 선택: 유전자 검색·선택 → 각 유전자에 Amplification / Deletion 지정 */
export function CnvSelect({ field, cnv, addCnvGene, setCnvType, removeCnvGene }) {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const opts = field.options || [];          // 검색용 전체 유전자(백엔드 cnvGenes)
    const repGenes = field.repGenes || [];      // 대표 유전자(고정 버튼)
    const ql = q.trim().toLowerCase();
    const chosenGenes = cnv.map((c) => c.gene);
    const searchHits = ql
        ? opts.filter((g) => repGenes.indexOf(g) === -1 && chosenGenes.indexOf(g) === -1 && g.toLowerCase().includes(ql)).slice(0, 30)
        : [];

    const geneChip = (gene) => {
        const c = cnv.find((x) => x.gene === gene);
        const active = !!c;
        return (
            <button key={gene}
                className={active ? `${styles.chip} ${styles.chipActive}` : styles.chip}
                onClick={() => { if (active) removeCnvGene(gene); else addCnvGene(gene); }}
                title={active ? t('클릭하여 해제') : t('클릭하여 추가')}>
                {gene}{active ? ` · ${c.type === "Amplification" ? "Amp" : "Del"}` : ""}
            </button>
        );
    };

    return (
        <div>
            <div className={styles.fieldLabel}>{t(field.label)}</div>
            <div className={styles.chipSearch}>
                <Icon.Search />
                <input className={styles.chipSearchInput} placeholder={t('유전자 검색 (전체)')}
                    value={q} onChange={(e) => setQ(e.target.value)} />
                {q && <button className={styles.chipSearchClear} onClick={() => setQ("")}><Icon.X /></button>}
            </div>
            {ql && (
                /* mutation 과 동일 — 선택해도 검색 목록을 닫지 않는다(연속 선택) */
                <div className={styles.chipSearchResult}>
                    {searchHits.length > 0 ? searchHits.map((g) => (
                        <button key={g} className={styles.chipSearchHit} onClick={() => addCnvGene(g)}>
                            <Icon.Plus /> {g}
                        </button>
                    )) : <span className={styles.fieldHint}>{t('검색 결과 없음')}</span>}
                </div>
            )}
            {/* 선택된 유전자: Amp/Del 토글 (칩 아래 표시) */}
            {cnv.length > 0 && (
                <div className={styles.cnvSelWrap}>
                    {cnv.map((c) => (
                        <div key={c.gene} className={styles.cnvSelRow}>
                            <span className={styles.cnvSelGene}>{c.gene}</span>
                            <div className={styles.cnvToggle}>
                                <button className={c.type === "Amplification" ? `${styles.cnvBtn} ${styles.cnvBtnAmp}` : styles.cnvBtn}
                                    onClick={() => setCnvType(c.gene, "Amplification")}>Amp</button>
                                <button className={c.type === "Deletion" ? `${styles.cnvBtn} ${styles.cnvBtnDel}` : styles.cnvBtn}
                                    onClick={() => setCnvType(c.gene, "Deletion")}>Del</button>
                            </div>
                            <button className={styles.cnvRemove} onClick={() => removeCnvGene(c.gene)}><Icon.X /></button>
                        </div>
                    ))}
                </div>
            )}
            {/* 검색으로 추가된 비대표 (선택 안 된 경우엔 안 보임 → 위 토글에 이미 있음) */}
            {/* 대표 유전자 (가로 칩, mutation 과 동일 스타일) */}
            <div className={styles.chipRow} style={cnv.length > 0 ? { marginTop: 6 } : undefined}>
                {repGenes.map((g) => geneChip(g))}
            </div>
        </div>
    );
}

/* 드롭다운 + 체크박스 다중선택 (subtype 등). 검색 지원. */
export function DropdownCheckSelect({ field, chipValue, onToggle }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const opts = field.options || [];
    const ql = q.trim().toLowerCase();
    const shown = ql
        ? opts.filter((o) => labelFor(o).toLowerCase().includes(ql) || String(o).toLowerCase().includes(ql))
        : opts;

    return (
        <div className={styles.ddWrap}>
            <div className={styles.fieldLabel}>{t(field.label)}</div>
            <button className={styles.ddToggle} onClick={() => setOpen((v) => !v)}>
                <span>{chipValue.length > 0 ? `${chipValue.length}${t('개 선택')}` : t('선택')}</span>
                <span className={styles.ddCaret}>{open ? "▲" : "▼"}</span>
            </button>
            {/* 선택된 항목 칩 (닫혀 있어도 표시) */}
            {chipValue.length > 0 && (
                <div className={styles.chipRow} style={{ marginTop: 4 }}>
                    {chipValue.map((opt) => (
                        <button key={opt} onClick={() => onToggle(opt)} className={`${styles.chip} ${styles.chipActive}`}>
                            {t(labelFor(opt))} <Icon.X />
                        </button>
                    ))}
                </div>
            )}
            {open && (
                <div className={styles.ddPanel}>
                    <div className={styles.chipSearch}>
                        <Icon.Search />
                        <input className={styles.chipSearchInput} placeholder={t('검색')}
                            value={q} onChange={(e) => setQ(e.target.value)} />
                        {q && <button className={styles.chipSearchClear} onClick={() => setQ("")}><Icon.X /></button>}
                    </div>
                    <div className={styles.ddList}>
                        {shown.length > 0 ? shown.map((opt) => (
                            <label key={opt} className={styles.ddItem}>
                                <input type="checkbox" checked={chipValue.includes(opt)} onChange={() => onToggle(opt)} />
                                <span>{t(labelFor(opt))}</span>
                            </label>
                        )) : <span className={styles.fieldHint}>{t('검색 결과 없음')}</span>}
                    </div>
                </div>
            )}
        </div>
    );
}

/* 칩 선택: 옵션 많으면 상위 N개만 + 검색으로 더 찾기.
 * - 선택된 항목은 항상 상단에 노출 (클릭 해제)
 * - 검색 결과에서 고르면 검색어 자동 클리어 → 기본 리스트로 복귀, 연속 선택 자연스러움
 * - 검색 중이 아니면 상위 N개(+더보기) 기본 노출 */
export function ChipSelect({ field, chipValue, onToggle }) {
    const { t } = useTranslation();
    const VISIBLE = 8;
    const [q, setQ] = useState("");
    const [showAll, setShowAll] = useState(false);
    const opts = field.options || [];
    const many = opts.length > VISIBLE;
    const ql = q.trim().toLowerCase();

    // 선택해도 검색어를 지우지 않는다 → 검색 목록이 열린 채로 남아 연속 선택이 된다.
    const pick = (opt) => { onToggle(opt); };

    // 검색 후보 (검색 중일 때만)
    const searchHits = ql
        ? opts.filter((o) => !chipValue.includes(o) &&
            (labelFor(o).toLowerCase().includes(ql) || String(o).toLowerCase().includes(ql)))
        : [];

    // 기본 후보 (검색 중이 아닐 때: 상위 N 또는 전체, 선택된 것 제외)
    const baseList = (showAll ? opts : opts.slice(0, VISIBLE)).filter((o) => !chipValue.includes(o));

    return (
        <div>
            <div className={styles.fieldLabel}>{t(field.label)}</div>
            {many && (
                <div className={styles.chipSearch}>
                    <Icon.Search />
                    <input className={styles.chipSearchInput} placeholder={t('검색해서 추가')}
                        value={q} onChange={(e) => setQ(e.target.value)} />
                    {q && <button className={styles.chipSearchClear} onClick={() => setQ("")}><Icon.X /></button>}
                </div>
            )}

            {/* 검색 결과 드롭다운 (검색 중일 때만, 기본 리스트 위에) */}
            {ql && (
                <div className={styles.chipSearchResult}>
                    {searchHits.length > 0 ? searchHits.map((opt) => (
                        <button key={opt} onClick={() => pick(opt)} className={styles.chipSearchHit}>
                            <Icon.Plus /> {t(labelFor(opt))}
                        </button>
                    )) : <span className={styles.fieldHint}>{t('검색 결과 없음')}</span>}
                </div>
            )}

            {/* 선택된 항목 (항상 상단) */}
            {chipValue.length > 0 && (
                <div className={styles.chipRow}>
                    {chipValue.map((opt) => (
                        <button key={opt} onClick={() => onToggle(opt)} className={`${styles.chip} ${styles.chipActive}`}>
                            {t(labelFor(opt))}
                        </button>
                    ))}
                </div>
            )}

            {/* 기본 후보 리스트 (항상 노출 — 검색과 무관하게 유지) */}
            <div className={styles.chipRow} style={chipValue.length > 0 ? { marginTop: 4 } : undefined}>
                {baseList.map((opt) => (
                    <button key={opt} onClick={() => onToggle(opt)} className={styles.chip}>
                        {t(labelFor(opt))}
                    </button>
                ))}
            </div>

            {many && (
                <button className={styles.linkBtn} onClick={() => setShowAll((s) => !s)}>
                    {showAll ? t('접기') : `+${opts.length - VISIBLE}${t('개 더 보기')}`}
                </button>
            )}
        </div>
    );
}

/* hgvsp_short 2차 필터 패널 (검색 + 체크박스) */
export function HgvspPanel({ gene, variants, selected, onToggle }) {
    const { t } = useTranslation();
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const filtered = variants.filter((v) => v.toLowerCase().includes(q.toLowerCase()));
    return (
        <div className={styles.hgvspPanel}>
            <button className={styles.hgvspHeader} onClick={() => setOpen((o) => !o)}>
                <span className={styles.hgvspGene}>{gene}</span>
                <span className={styles.hgvspMeta}>
                    {selected.length > 0 ? `${selected.length}${t('개 선택')}` : t('전체 변이')}
                    <span className={open ? styles.chevOpen : styles.chevClosed}><Icon.Chevron /></span>
                </span>
            </button>
            {open && (
                <div className={styles.hgvspBody}>
                    <div className={styles.hgvspSearch}>
                        <Icon.Search />
                        <input className={styles.hgvspInput} placeholder={t('변이 검색 (hgvsp_short)')}
                            value={q} onChange={(e) => setQ(e.target.value)} />
                    </div>
                    <div className={styles.hgvspList}>
                        {filtered.length === 0 && <span className={styles.fieldHint}>{t('변이 목록 없음')}</span>}
                        {filtered.map((v) => (
                            <label key={v} className={styles.hgvspItem}>
                                <input type="checkbox" className={styles.checkbox}
                                    checked={selected.includes(v)} onChange={() => onToggle(v)} />
                                <span className={styles.mono10}>{v}</span>
                            </label>
                        ))}
                    </div>
                    {selected.length === 0 && variants.length > 0 &&
                        <div className={styles.fieldHint}>{gene} — {t('선택하지 않으면 모든 변이 포함')}</div>}
                </div>
            )}
        </div>
    );
}

/* =============================================================
 * 활성 필터 자연어 표시 (3-state NOT 포함)
 * ============================================================= */